

export interface DashboardRow {
  id: string;
  createdAt: string;
  enquiryDate: string;
  source: string | null;
  name: string | null;
  phone: string | null;
  email: string | null;
  location: string | null;
  requirement: string | null;
  applicationType: string | null;
  businessSegment: string | null;
  verifiedStatus: 'verified' | 'unverified';
  verifiedBy: 'chatkart' | 'manual' | 'avika' | null;
  verifiedByName: string | null;
  /** 2026-10-09 — LeadImport.verificationStatus. A verified row whose details
   *  are incomplete (detailsComplete false) gets "Re-check details". Optional:
   *  an older server does not send them. */
  verificationStatus?: string | null;
  detailsComplete?: boolean;
  assignedToName: string | null;
  /** Who made the lead (LeadImport.createdByUserId) — the Self-made tab's "Created By". */
  createdByName?: string | null;
  coordinatorName?: string | null;
  teamName: string | null;
  // L238 — fulfilling dealer, PARALLEL to the rep (a dealer never removes a
  // row from Unassigned; only a human owner does).
  assignedDealerId?: string | null;
  assignedDealerName?: string | null;
  assignedDealerManagerName?: string | null;
  dealerAssignedAt?: string | null;
  dealerNotifiedAt?: string | null;
  dealerNotifiedByUserId?: string | null;
  dealerNotifiedByName?: string | null;
  // ── Avika (2026-06-10) enrichment ──────────────────────────────────────
  score: number | null;
  tier: 'HOT' | 'WARM' | 'COLD' | null;
  scoringModel: 'end_customer' | 'no_price' | 'b2b' | null;
  capacity: string | null;
  capacitySource: string | null;
  quantity: string | null;
  timeline: string | null;
  priceShared: boolean;
  priceRange: string | null;
  state: string | null;
  waLineLabel: 'line1' | 'line2' | null;
  isB2bPriority: boolean;
  isOutOfBudget: boolean;
  // SUN-233 — Rejected tab only (server sends these when tab=rejected).
  rejectedAt?: string | null;
  rejectReason?: string | null;
  rejectedByName?: string | null;
}

export interface VerifySchemaField {
  key: string;
  label: string;
  type: 'select' | 'text' | 'textarea' | 'boolean';
  options?: string[];
  source?: 'products' | 'locations';
  required?: boolean;
  placeholder?: string;
  /** Render at full modal width (textareas default to true). */
  wide?: boolean;
  /** Conditional visibility: render/submit only when values[key] === equals. */
  showWhen?: { key: string; equals: string };
}

export const B2B_ONLY = { key: 'applicationType', equals: 'B2B' } as const;

export const FALLBACK_VERIFY_SCHEMA: VerifySchemaField[] = [
  { key: 'name',            label: 'Customer name', type: 'text' },
  { key: 'product',         label: 'Product', type: 'select', source: 'products', wide: true },
  { key: 'applicationType', label: 'Application type', type: 'select', options: ['Residential', 'Commercial', 'B2B'] },
  // B2B sub-fields — the b2b scoring model's inputs (shown only for B2B).
  { key: 'b2bBusinessName',    label: 'Firm / business name', type: 'text', showWhen: B2B_ONLY },
  { key: 'b2bBusinessType',    label: 'Business type / trade', type: 'text', placeholder: 'e.g. Sanitary shop', showWhen: B2B_ONLY },
  { key: 'b2bGst',             label: 'GSTIN', type: 'text', showWhen: B2B_ONLY },
  { key: 'b2bHasGst',          label: 'Has GST (not shared)?', type: 'boolean', showWhen: B2B_ONLY },
  { key: 'b2bYearsInBusiness', label: 'Years in business', type: 'text', showWhen: B2B_ONLY },
  { key: 'b2bHasShowroom',     label: 'Has showroom?', type: 'boolean', showWhen: B2B_ONLY },
  { key: 'b2bExpectedVolume',  label: 'Expected volume', type: 'text', placeholder: 'e.g. 20 tanks/month', showWhen: B2B_ONLY },
  { key: 'leadCapacity',    label: 'Capacity / quantity', type: 'select',
    options: ['500 L', '1000 L', '1500 L', '2000 L', '2500 L', '3000 L', '4000 L', '5000 L', 'Custom (>5000L)'] },
  { key: 'leadTimeline',    label: 'Purchase timeline', type: 'select',
    options: ['within 7 days', 'within 30 days', 'within 90 days', 'exploring'] },
  { key: 'indicativePriceShared', label: 'Indicative price shared with customer?', type: 'boolean' },
  { key: 'continuedAfterPrice',   label: 'Customer continued after hearing price?', type: 'boolean' },
  { key: 'indicativePriceRange',  label: 'Price range quoted (note)', type: 'text', placeholder: 'e.g. ₹80k–₹95k' },
  { key: 'location',        label: 'City / location', type: 'text', placeholder: 'e.g. Panipat' },
  { key: 'locationState',   label: 'State', type: 'select', source: 'locations' },
  { key: 'ownerPhone',      label: "Owner's phone number", type: 'text', placeholder: 'Required for a HOT lead' },
  { key: 'isDecisionMaker', label: 'Is the decision maker?', type: 'boolean' },
  { key: 'notes',           label: 'Notes (internal)', type: 'textarea', placeholder: 'Any context the team should know', wide: true },
];

export function normalizeVerifyField(f: any): VerifySchemaField | null {
  const type = (f?.type ?? f?.input) as string;
  if (!f?.key || !type || type === 'derived' || f.readOnly) return null;
  const source = f.source === 'catalog' ? 'products' : f.source === 'gazetteer' ? 'locations' : f.source;
  return {
    key: String(f.key),
    label: String(f.label ?? f.key),
    type: (['select', 'text', 'textarea', 'boolean'].includes(type) ? type : 'text') as VerifySchemaField['type'],
    options: Array.isArray(f.options) ? f.options.map(String) : undefined,
    source,
    // 2026-10-08: keep the schema's required flag so the dialog marks it "*".
    required: f.required === true ? true : undefined,
    placeholder: f.placeholder,
    wide: f.wide,
    showWhen: f.showWhen && typeof f.showWhen.key === 'string' ? f.showWhen : undefined,
  };
}

export const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chandigarh',
  'Chhattisgarh', 'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh',
  'Jammu & Kashmir', 'Jharkhand', 'Karnataka', 'Kerala', 'Ladakh',
  'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Puducherry', 'Punjab', 'Rajasthan', 'Sikkim',
  'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand',
  'West Bengal',
];

export type VerifyValues = Record<string, string>;

export interface DashboardResponse {
  success: true;
  data: DashboardRow[];
  total: number;
  tab: string;
  limit: number;
  offset: number;
}

export interface HotOverdueItem {
  id: string;
  name: string | null;
  phone: string | null;
  location: string | null;
  product: string | null;
  score: number | null;
  hotSince: string;
  // L238 — set client-side after a dealer assign on the Overdue tab.
  assignedDealerName?: string | null;
}

export function formatHotAge(hotSinceIso: string): string {
  const ms = Date.now() - new Date(hotSinceIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 24) return `${Math.max(hours, 1)}h`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

export interface DedupMatch {
  existingLeadId: string;
  name: string | null;
  assignedToName: string | null;
  sourceName: string | null;
  location: string | null;
  /** 2026-10-22 — a B2B dealer prospect lives in the B2B tab (hidden from Unassigned). */
  applicationType?: string | null;
  dealerStage?: string | null;
  /** 2026-10-09 — set when the match is a LOST lead (reopen instead of a new lead). */
  lost?: import('./LostLeadReopen').LostLeadInfo;
  canReopen?: boolean;
}

export type Tab = 'unassigned' | 'assigned' | 'self_made' | 'b2b' | 'dealer_assigned' | 'rejected' | 'hot_overdue';

export const TABS: { key: Tab; label: string }[] = [
  { key: 'unassigned', label: 'Unassigned' },
  { key: 'assigned', label: 'Assigned' },
  { key: 'self_made', label: 'Self-made' },
  { key: 'b2b', label: 'B2B' },
  { key: 'dealer_assigned', label: 'Dealer assigned' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'hot_overdue', label: 'Overdue HOT' },
];

export function formatCapacitySummary(capacity: string | null, quantity?: string | number | null): string {
  const q = Number(quantity);
  const cap = String(capacity || '').trim();
  if (!cap) return '';
  if (!Number.isFinite(q) || q <= 1) return cap;
  return `${q} nos × ${cap}`;
}

export function buildLeadSummary(r: DashboardRow): string {
  const L: string[] = [];
  L.push(`Lead: ${r.name || 'Unknown'}`);
  if (r.phone) L.push(`Phone: ${r.phone}`);
  if (r.email) L.push(`Email: ${r.email}`);
  L.push(`Enquiry date: ${formatDate(r.createdAt)}`);
  const line = r.waLineLabel === 'line1' ? 'Line 1' : r.waLineLabel === 'line2' ? 'Line 2' : null;
  L.push(`Source: ${r.source || 'Unknown'}${line ? ` (${line})` : ''}`);
  const loc = [r.location, r.state].filter(Boolean).join(', ');
  if (loc) L.push(`Location: ${loc}`);
  if (r.requirement) L.push(`Requirement: ${r.requirement}`);
  if (r.applicationType) L.push(`Application: ${r.applicationType}${r.businessSegment ? ` (${r.businessSegment})` : ''}`);
  if (r.capacity) L.push(`Capacity: ${formatCapacitySummary(r.capacity, r.quantity)}`);
  if (r.timeline) L.push(`Timeline: ${r.timeline}`);
  if (r.priceShared) L.push(`Indicative price shared: ${r.priceRange || 'Yes'}`);
  if (r.score != null) {
    const flags = [r.isB2bPriority ? 'B2B PRIORITY' : '', r.isOutOfBudget ? 'OUT OF BUDGET' : ''].filter(Boolean).join(', ');
    L.push(`Lead score: ${r.score}${r.tier ? ` (${r.tier})` : ''}${flags ? ` — ${flags}` : ''}`);
  }
  if (r.scoringModel) L.push(`Scoring model: ${r.scoringModel}`);
  const verified = r.verifiedStatus === 'verified'
    ? `Verified${r.verifiedBy ? ` (${r.verifiedBy}${r.verifiedByName ? ': ' + r.verifiedByName : ''})` : ''}`
    : 'Unverified';
  L.push(`Status: ${verified}`);
  if (r.teamName || r.assignedToName) L.push(`Assigned: ${r.teamName || r.assignedToName}`);
  return L.join('\n');
}

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}-${MONTHS[d.getMonth()]}`;
}

export function formatDealerDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value ?? '';
  return `${part('day')}-${part('month')}, ${part('hour')}:${part('minute')} ${part('dayPeriod')}`;
}

// SUN-233 — the reasons Triage can reject with, in the words a coordinator uses.
const REJECT_REASON_LABELS: Record<string, string> = {
  not_interested: 'Not interested', spam: 'Spam', out_of_scope: 'Out of scope', wrong_number: 'Wrong number',
  dnd: 'Do not disturb', invalid_number: 'Invalid number', duplicate: 'Duplicate', irrelevant: 'Irrelevant',
};
export function rejectReasonLabel(code: string | null | undefined): string {
  if (!code) return '—';
  return REJECT_REASON_LABELS[code] ?? code.replace(/_/g, ' ');
}

/** "26 Oct 2026" in IST — the day the lead was rejected. */
export function formatRejectedOn(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' }).format(d);
}

export interface Toast {
  id: string;
  text: string;
  undo?: () => void;
}

export type SortKey =
  | 'newest' | 'oldest'
  | 'name_asc' | 'name_desc'
  | 'source_asc' | 'source_desc'
  | 'location_asc' | 'location_desc'
  // Avika (2026-06-10) — lead-score sorts.
  | 'score_desc' | 'score_asc'
  // LBR #12 (2026-07-25) — last-reply sort (latest WhatsApp/Avika reply first).
  | 'last_reply_desc';

export type VerifiedFilter = 'any' | 'verified' | 'unverified';

export type VerifiedSourceFilter = 'any' | 'chatkart' | 'manual';

export type TierFilter = 'any' | 'HOT' | 'WARM' | 'COLD' | 'BLANK';

export type LineFilter = 'all' | 'line1' | 'line2';

export interface FilterState {
  dateFrom: string;
  dateTo: string;
  sources: string[];
  applicationTypes: string[];
  businessSegments: string[];
  verified: VerifiedFilter;
  verifiedSource: VerifiedSourceFilter;
  tier: TierFilter;
  location: string;
}

export const BLANK_FILTERS: FilterState = {
  dateFrom: '', dateTo: '',
  sources: [], applicationTypes: [], businessSegments: [],
  verified: 'any', verifiedSource: 'any', tier: 'any',
  location: '',
};

export const SOURCE_OPTIONS = ['WEBSITE', 'INDIAMART', 'AI CHATBOX'];

export const APP_TYPE_OPTIONS = ['Residential', 'Commercial', 'B2B'];

export const SEGMENT_OPTIONS = ['B2B', 'B2C'];

export function presetRange(preset: string): [string, string] {
  const today = new Date();
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  const to = fmt(today);
  if (preset === 'today') return [to, to];
  if (preset === '7d')  { const d = new Date(today); d.setDate(d.getDate() - 7);  return [fmt(d), to]; }
  if (preset === '30d') { const d = new Date(today); d.setDate(d.getDate() - 30); return [fmt(d), to]; }
  if (preset === 'mtd') { const d = new Date(today.getFullYear(), today.getMonth(), 1); return [fmt(d), to]; }
  return ['', ''];
}

export function buildTriageFilterParams(opts: {
  tab: Tab; search: string; sortKey: SortKey; filters: FilterState; lineFilter: LineFilter;
}): URLSearchParams {
  const { tab, search, sortKey, filters, lineFilter } = opts;
  const q = new URLSearchParams({ tab, sort: sortKey });
  if (search.trim().length >= 2) q.set('search', search.trim());
  if (filters.dateFrom)                 q.set('dateFrom', filters.dateFrom);
  if (filters.dateTo)                   q.set('dateTo', filters.dateTo);
  if (filters.sources.length)           q.set('source', filters.sources.join(','));
  if (filters.applicationTypes.length)  q.set('applicationType', filters.applicationTypes.join(','));
  if (filters.businessSegments.length)  q.set('businessSegment', filters.businessSegments.join(','));
  if (filters.location.trim().length >= 2) q.set('location', filters.location.trim());
  if (filters.verified !== 'any')       q.set('verified', filters.verified);
  if (filters.verifiedSource !== 'any') q.set('verifiedSource', filters.verifiedSource);
  if (filters.tier !== 'any')           q.set('tier', filters.tier);
  if (lineFilter !== 'all')             q.set('line', lineFilter);
  return q;
}

export const ADD_LEAD_SOURCES = [
  { key: 'phone-in', label: 'Phone-in' },
  { key: 'walk-in', label: 'Walk-in' },
  { key: 'reference', label: 'Reference' },
] as const;

export type AddLeadSource = (typeof ADD_LEAD_SOURCES)[number]['key'];

/**
 * 2026-10-22 (Deepika) — which triage tab holds the lead the New lead box matched.
 * A B2B dealer prospect is hidden from the consumer tabs (dealer-scope rule), so it
 * must open in B2B; a self-made lead in Self-made; an owned lead in Assigned.
 */
export function tabForExistingMatch(m: Pick<DedupMatch, 'sourceName' | 'assignedToName' | 'applicationType' | 'dealerStage'>): Tab {
  if ((m.applicationType ?? '').toUpperCase() === 'B2B' || m.dealerStage) return 'b2b';
  const MANUALS = ['MANUAL', 'manual', 'walkin', 'mobile-capture', 'self_made'];
  if (m.sourceName && MANUALS.includes(m.sourceName)) return 'self_made';
  return m.assignedToName ? 'assigned' : 'unassigned';
}
